/**
 * Submission Triage toolbar — responsive.
 *
 * Desktop (≥ md):
 *   [📄 Submissions] [🏢 Facilities]   [☰ Filters]  [⚙ Customize]   [Search input]
 *
 * The search placeholder names the fields the box is meant to match (OR-1386
 * design): CCN, Address, Facility Name, Zip. The backend side of that search
 * is OR-1387 — until it ships, only the submission title and FNI match.
 *
 * Mobile (< md):
 *   [Submissions ▾]   [☰] [⚙]
 *   [Search input — full width]
 *   ↑ tabs collapse   ↑ icon-only buttons; search lives below the row
 *                       so it's always visible (no modal hop).
 */
import type { ReactNode } from "react";
import { Building07, File04, FilterLines, SearchLg, Settings04 } from "@untitledui/icons";
import { Input, NativeSelect, Tabs, cx } from "@tricura/atlas-ui";

type TriageView = "submissions" | "facilities";

interface TriageToolbarProps {
    view: TriageView;
    onViewChange: (next: TriageView) => void;
    search: string;
    onSearchChange: (next: string) => void;
    onOpenFilters?: () => void;
    onOpenCustomize?: () => void;
    /** Active-filter count rendered as a badge on the Filters button. */
    filterCount?: number;
    /** The Saved Views control (design OR-1507). Passed in rather than
     *  built here so the toolbar stays presentational — the menu needs
     *  the page's view collection and applied set. Desktop and mobile
     *  get their own instance because the trigger collapses to an icon
     *  below md. */
    savedViews?: ReactNode;
    savedViewsCompact?: ReactNode;
    /** Held while the user's saved layout is still loading. Touching
     *  Filters or Customize before it lands would either be dropped or be
     *  overwritten by hydration a moment later. */
    isDisabled?: boolean;
}

/** Source of truth for the view picker. Same shape feeds the desktop
 *  `Tabs` (`button-minimal`) and the mobile `NativeSelect` so both
 *  surfaces stay in lock-step. */
const VIEW_TABS = [
    { id: "submissions", label: "Submissions", icon: File04, "data-autotest": "triage-toolbar-tab-submissions"},
    { id: "facilities", label: "Facilities", icon: Building07, "data-autotest": "triage-toolbar-tab-facilities" },
];

export function TriageToolbar({ view, onViewChange, search, onSearchChange, onOpenFilters, onOpenCustomize, filterCount = 0, savedViews, savedViewsCompact, isDisabled = false }: TriageToolbarProps) {
    return (
        <div className="border-secondary bg-primary border-b px-4 py-3 md:px-4">
            {/* ---------- Desktop layout (≥ md) ---------- */}
            <div className="hidden flex-wrap items-center gap-2 md:flex md:flex-nowrap">
                <Tabs
                    selectedKey={view}
                    onSelectionChange={(k) => onViewChange(k as TriageView)}
                    className="w-max"
                >
                    <Tabs.List type="button-minimal" items={VIEW_TABS}>
                        {(tab) => <Tabs.Item {...tab} />}
                    </Tabs.List>
                </Tabs>

                <div className="ml-1 flex items-center gap-2">
                    <ToolbarButton icon={FilterLines} label="Filters" badge={filterCount} isDisabled={isDisabled} data-autotest="triage-toolbar-button-filters" onClick={() => onOpenFilters?.()} />
                    <ToolbarButton icon={Settings04} label="Customize" isDisabled={isDisabled} data-autotest="triage-toolbar-button-customize" onClick={() => onOpenCustomize?.()} />
                    {savedViews}
                </div>

                <div className="md:ml-auto md:w-96">
                    <Input
                        size="sm"
                        icon={SearchLg}
                        placeholder="Search by CCN, Address, Facility Name, or Zip"
                        value={search}
                        onChange={onSearchChange}
                        aria-label="Search submissions"
                        data-autotest="triage-toolbar-input-search"
                    />
                </div>
            </div>

            {/* ---------- Mobile layout (< md) ---------- */}
            <div className="flex flex-col gap-2 md:hidden">
                <div className="flex items-center gap-2">
                    <NativeSelect
                        size="sm"
                        aria-label="View"
                        value={view}
                        onChange={(e) => onViewChange(e.target.value as TriageView)}
                        options={VIEW_TABS.map((t) => ({ label: t.label, value: t.id }))}
                        className="flex-1"
                    />
                    <IconBtn icon={FilterLines} label="Filters" badge={filterCount} isDisabled={isDisabled} data-autotest="triage-toolbar-button-filters" onClick={() => onOpenFilters?.()} />
                    <IconBtn icon={Settings04} label="Customize" isDisabled={isDisabled} data-autotest="triage-toolbar-button-customize" onClick={() => onOpenCustomize?.()} />
                    {savedViewsCompact}
                </div>
                <Input
                    size="sm"
                    icon={SearchLg}
                    placeholder="Search by CCN, Address, Facility Name, or Zip"
                    value={search}
                    onChange={onSearchChange}
                    aria-label="Search submissions"
                    data-autotest="triage-toolbar-input-search"
                />
            </div>
        </div>
    );
}

const PILL =
    "border-primary inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border bg-primary px-3 text-sm font-semibold transition duration-100 ease-linear";

function ToolbarButton({
    icon: Icon,
    label,
    badge,
    onClick,
    isDisabled,
    "data-autotest": dataAutotest,
}: {
    icon: typeof FilterLines;
    label: string;
    badge?: number;
    onClick: () => void;
    isDisabled?: boolean;
    "data-autotest": string;
}) {
    return (
        <button type="button" onClick={onClick} disabled={isDisabled} data-autotest={dataAutotest} className={cx(PILL, "text-primary shadow-xs hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50")}>
            <Icon aria-hidden="true" className="size-4 text-fg-quaternary stroke-[2.25px]" />
            {label}
            {badge != null && badge > 0 && <CountBadge value={badge} />}
        </button>
    );
}

function IconBtn({
    icon: Icon,
    label,
    badge,
    onClick,
    isDisabled,
    "data-autotest": dataAutotest,
}: {
    icon: typeof FilterLines;
    label: string;
    badge?: number;
    onClick: () => void;
    isDisabled?: boolean;
    "data-autotest"?: string;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            aria-label={label}
            data-autotest={dataAutotest}
            className="border-primary size-9 bg-primary text-fg-quaternary shadow-xs hover:bg-secondary hover:text-fg-secondary relative inline-flex shrink-0 cursor-pointer items-center justify-center rounded-lg border transition duration-100 ease-linear disabled:cursor-not-allowed disabled:opacity-50"
        >
            <Icon aria-hidden="true" className="size-4 stroke-[2.25px]" />
            {badge != null && badge > 0 && (
                <span className="bg-fg-brand-primary absolute -top-1 -right-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-semibold leading-none text-white">
                    {badge}
                </span>
            )}
        </button>
    );
}

/** Inline badge used by the desktop Filters button. */
function CountBadge({ value }: { value: number }) {
    return (
        <span className="bg-fg-brand-primary inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold leading-none text-white">
            {value}
        </span>
    );
}
